import { get, isEmpty } from "lodash";
import { Link } from "react-router-dom";
import { Controller, useFormContext } from "react-hook-form";
import {
  IPickupAttractionParticipantDetail,
  IPickupAttractionParticipantForm,
} from "interfaces";
import { HttpError, useOne, useTranslation } from "@refinedev/core";
import { CollapsibleSection } from "components/collapsibleSection";
import Input from "components/input";
import Select from "components/select";

export const EditInformationSection = ({
  participantDetailData,
}: {
  participantDetailData: IPickupAttractionParticipantDetail | null;
}) => {
  const { translate } = useTranslation();

  const { data: categoryData, isFetching: isFetchingCategoryData } = useOne<
    any[],
    HttpError
  >({
    resource: `events/${participantDetailData?.event?.id}/categories`,
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!participantDetailData?.event?.id,
    },
  });

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<IPickupAttractionParticipantForm>(); // retrieve all hook methods

  const languages = get(participantDetailData, "event.languages", []);

  const categoryIds = (categoryData?.data ?? []).map(
    (category: { [x: string]: string }) => ({
      value: isEmpty(languages)
        ? get(category, "category_en", "")
        : get(category, `category_${languages[0]}`, ""),
      label: isEmpty(languages)
        ? get(category, "category_en", "")
        : get(category, `category_${languages[0]}`, ""),
    }),
  );

  return (
    <CollapsibleSection label={translate("information")} defaultOpen={true}>
      <div
        className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 grid-cols-12 lg:gap-x-6"
        style={{ wordBreak: "break-word" }}
      >
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-1">
          <div className="col-span-2">
            <p className="font-medium">{translate("checkin.participant-id")}</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.participant_id}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-3">
          <div className="col-span-2">
            <p className="font-medium">{translate("seat")}</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.seat_number}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-5">
          <div className="col-span-2">
            <p className="font-medium">{translate("order-code")}</p>
          </div>
          <div className="col-span-4">
            <Link
              to={`/orders/${participantDetailData?.participant.order_code}`}
              className="w-fit font-medium text-brand-500 hover:underline"
            >
              {participantDetailData?.participant.order_code}
            </Link>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-7">
          <div className="col-span-2">
            <p className="font-medium">{translate("full-name")}</p>
          </div>
          <div className="col-span-4">
            <Input
              id="full_name"
              type="text"
              placeholder="Full name"
              error={errors["full_name"]}
              {...register("full_name")}
            />
            {errors["full_name"] ? (
              <p className="error-text">
                <span className="font-medium">{errors?.full_name.message}</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-2">
          <div className="col-span-2">
            <p className="font-medium">{translate("checkin.event-name")}</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.event_name}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-4">
          <div className="col-span-2">
            <p className="font-medium">{translate("ticket-name")}</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.ticket_name_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-6">
          <div className="col-span-2">
            <p className="font-medium">{translate("ticket-no")}</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.ticket_no}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-8">
          <div className="col-span-2">
            <p className="font-medium">{translate("checkin.category")}</p>
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="category"
              render={({ field: { onChange, value = "", ref } }) => (
                <Select
                  key="category"
                  inputRef={ref}
                  options={categoryIds}
                  id="category"
                  placeholder="Select Category"
                  defaultValue={
                    categoryIds.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value,
                    ) ?? ""
                  }
                  value={categoryIds.find(
                    (selected: { label: string; value: any }) =>
                      selected.value === value,
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={errors["category"]}
                  isLoading={isFetchingCategoryData}
                />
              )}
            />
            {errors["category"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["category"]?.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
